import React, { useState, useCallback } from 'react';
import { hoverable } from '../../themes/utils/responsiveHover';
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Skeleton,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePlanStorage } from '../../hooks/usePlanStorage';
import { useSidebar } from '../../context/SidebarContext';
import SidebarItemMenu from './SidebarItemMenu';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { describeMissingRoles, describeArchivedRoles } from '../editor/features/plans/planMissingRoles';

const DEFAULT_VISIBLE = 5;
const LS_KEY_COLLAPSED = 'sidebar_plans_collapsed';

/**
 * SidebarPlansList — Shows user's workflow plans in the sidebar.
 * Each plan displays an icon + truncated name.
 * Click navigates to /editor/plans?planId=xxx to open Plan Editor with that plan.
 */
const SidebarPlansList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { plans, loading, deletePlan } = usePlanStorage();
  const { isMobile, closeOverlay } = useSidebar();

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [hoveredPlanId, setHoveredPlanId] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem(LS_KEY_COLLAPSED) === 'true');
  const [showAll, setShowAll] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(LS_KEY_COLLAPSED, String(next));
      return next;
    });
  }, []);

  const handlePlanClick = (plan) => {
    navigate(`/editor/plans?planId=${plan.id}`);
    if (isMobile) closeOverlay();
  };

  const handleMenuOpen = (event, plan) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedPlan(plan);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedPlan(null);
  };

  const handleEditPlan = (plan) => {
    navigate('/editor/plans?planId=' + plan.id);
    if (isMobile) closeOverlay();
  };

  const handleDeletePlan = async (plan) => {
    try {
      await deletePlan(plan.id);
    } catch (err) {
      console.error('[SidebarPlansList] Error deleting plan:', err);
    }
  };

  const handleNewPlan = () => {
    navigate('/editor/plans');
    if (isMobile) closeOverlay();
  };

  const hasMore = plans.length > DEFAULT_VISIBLE;
  const visiblePlans = showAll ? plans : plans.slice(0, DEFAULT_VISIBLE);
  const hiddenCount = plans.length - DEFAULT_VISIBLE;

  // #458 — el aviso por plan solo se ve con la sección desplegada Y el plan dentro
  // de los primeros DEFAULT_VISIBLE, así que un plan roto podía quedar invisible.
  // La cabecera lo sube: solo cuenta agentes BORRADOS, no archivados — un plan con
  // el agente archivado sigue ejecutándose y no es una rotura.
  const brokenCount = plans.filter((plan) => plan.hasMissingRoles).length;

  return (
    <Box data-testid="sidebar-plans-list">
      {/* Section header — clickable to collapse */}
      <Box
        onClick={toggleCollapsed}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCollapsed(); } }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          ...hoverable({ bgcolor: 'action.hover' }),
          borderRadius: 1,
          mx: 0.5,
        }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            color: 'text.disabled',
            mr: 0.5,
            transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 200ms ease',
          }}
        />
        <Typography
          variant="caption"
          sx={{
            flex: 1,
            color: 'text.disabled',
            textTransform: 'uppercase',
            fontWeight: 600,
            letterSpacing: 0.5,
          }}
        >
          {t('sidebar.plans', 'Planes')}
          {isCollapsed && plans.length > 0 && (
            <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 0.5, fontWeight: 400 }}>
              {plans.length}
            </Typography>
          )}
        </Typography>
        {brokenCount > 0 && (
          <Tooltip
            title={t('sidebar.plansBroken', { count: brokenCount })}
            arrow
            placement="right"
          >
            <WarningAmberIcon
              data-testid="sidebar-plans-broken-warning"
              aria-label={t('planList.brokenPlan.ariaLabel')}
              sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }}
            />
          </Tooltip>
        )}
        <Tooltip title={t('sidebar.newPlan', 'Nuevo plan')} placement="right" enterDelay={500}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleNewPlan(); }}
            aria-label={t('sidebar.newPlan', 'Nuevo plan')}
            sx={{
              color: 'text.secondary',
              p: 0.25,
              borderRadius: '4px',
              ...hoverable({ color: 'text.primary', bgcolor: 'action.selected' }),
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={!isCollapsed} timeout={200}>
        {/* Loading state */}
        {loading && (
          <Box sx={{ px: 2, py: 0.5 }}>
            {[1, 2].map((i) => (
              <Skeleton key={i} height={32} sx={{ mb: 0.25 }} />
            ))}
          </Box>
        )}

        {/* Plan list */}
        {!loading && plans.length > 0 && (
          <List disablePadding dense>
            {visiblePlans.map((plan) => {
              const isHovered = hoveredPlanId === plan.id;
              const isBroken = !!plan.hasMissingRoles;
              // El borrado manda: es el irreversible. El archivado solo se enseña
              // cuando es lo único que le pasa al plan.
              const isArchivedOnly = !isBroken && !!plan.hasArchivedRoles;
              return (
                <ListItem
                  key={plan.id}
                  disablePadding
                  onContextMenu={(e) => handleMenuOpen(e, plan)}
                  onMouseEnter={() => setHoveredPlanId(plan.id)}
                  onMouseLeave={() => setHoveredPlanId(null)}
                  sx={{ position: 'relative' }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, plan)}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: 8,
                      transform: 'translateY(-50%)',
                      opacity: { xs: 1, md: isHovered ? 1 : 0 },
                      transition: 'opacity 0.2s ease-in-out',
                      bgcolor: 'background.paper',
                      ...hoverable({ bgcolor: 'action.hover' }),
                      minWidth: { xs: 44, md: 28 },
                      minHeight: { xs: 44, md: 28 },
                      zIndex: 1,
                    }}
                  >
                    <MoreVertIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <ListItemButton
                    onClick={() => handlePlanClick(plan)}
                    data-testid="plan-list-item"
                    sx={{
                      minHeight: 30,
                      px: 1.5,
                      py: 0.125,
                      borderRadius: 1,
                      mx: 0.5,
                      paddingRight: '40px',
                      ...hoverable({ bgcolor: 'action.hover' }),
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: 'center' }}>
                      {/* #458 — el plan referencia un agente borrado: ni ejecutable
                          ni guardable hasta quitar esos pasos. */}
                      {isBroken ? (
                        <Tooltip title={describeMissingRoles(plan, t)} arrow placement="right">
                          <WarningAmberIcon
                            data-testid="plan-broken-warning"
                            aria-label={t('planList.brokenPlan.ariaLabel')}
                            sx={{ fontSize: 20, color: 'error.main' }}
                          />
                        </Tooltip>
                      ) : isArchivedOnly ? (
                        <Tooltip title={describeArchivedRoles(plan, t)} arrow placement="right">
                          <Inventory2OutlinedIcon
                            data-testid="plan-archived-warning"
                            aria-label={t('planList.archivedPlan.ariaLabel')}
                            sx={{ fontSize: 20, color: 'warning.main' }}
                          />
                        </Tooltip>
                      ) : (
                        <AccountTreeIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={plan.name}
                      primaryTypographyProps={{
                        variant: 'body2',
                        noWrap: true,
                        sx: { textOverflow: 'ellipsis' },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}

        {/* Show more / show less toggle */}
        {!loading && hasMore && (
          <Typography
            variant="caption"
            component="button"
            onClick={() => setShowAll((prev) => !prev)}
            sx={{
              display: 'block',
              px: 2,
              py: 0.5,
              color: 'text.disabled',
              cursor: 'pointer',
              border: 'none',
              bgcolor: 'transparent',
              textAlign: 'left',
              ...hoverable({ color: 'primary.main' }),
            }}
          >
            {showAll
              ? t('sidebar.showLess', 'Mostrar menos')
              : t('sidebar.plansShowMore', '{{count}} más...', { count: hiddenCount })}
          </Typography>
        )}

        {/* View all link */}
        {!loading && plans.length > 0 && (
          <Typography
            variant="caption"
            component="button"
            onClick={() => { navigate('/editor/plans'); if (isMobile) closeOverlay(); }}
            sx={{
              display: 'block',
              px: 2,
              py: 0.5,
              color: 'text.disabled',
              cursor: 'pointer',
              border: 'none',
              bgcolor: 'transparent',
              textAlign: 'left',
              ...hoverable({ color: 'primary.main' }),
            }}
          >
            {t('sidebar.plansViewAll', 'Gestionar planes')}
          </Typography>
        )}

        {/* Empty state */}
        {!loading && plans.length === 0 && (
          <Typography
            variant="caption"
            component="button"
            onClick={handleNewPlan}
            sx={{
              display: 'block',
              px: 2,
              py: 0.5,
              color: 'text.disabled',
              cursor: 'pointer',
              border: 'none',
              bgcolor: 'transparent',
              textAlign: 'left',
              ...hoverable({ color: 'text.primary' }),
            }}
          >
            {t('sidebar.plansEmpty', 'Crear tu primer plan')}
          </Typography>
        )}
      </Collapse>

      {/* Context menu */}
      <SidebarItemMenu
        anchorEl={menuAnchorEl}
        selectedItem={selectedPlan}
        onClose={handleMenuClose}
        onEdit={handleEditPlan}
        onDelete={handleDeletePlan}
        editLabel={t('sidebar.planMenu.edit', 'Editar')}
        deleteLabel={t('sidebar.planMenu.delete', 'Eliminar')}
        deleteConfirmTitle={t('sidebar.planMenu.deleteTitle', 'Eliminar plan')}
        deleteConfirmMessage={t('sidebar.planMenu.deleteMessage', '¿Estás seguro de que quieres eliminar este plan?')}
        deleteConfirmText={t('sidebar.planMenu.deleteConfirm', 'Eliminar')}
      />
    </Box>
  );
};

export default SidebarPlansList;
